import { EmptyState } from './EmptyState'
import { Input } from '../../../../components/Input'
import { ReactComponent as Search16 } from 'assets/svg/Search16.svg'
import { ReactComponent as Close16 } from 'assets/svg/Clos16.svg'
import { Table } from '../../../../components/Table'
import {
  TVendorListParams,
  TVendorsListFiltersFunc
} from '../VendorsList/types'
import { columns } from './constants'
import { isTableHasParams } from '../../../../helper/common'
import { useEffect, useRef, useState } from 'react'
import { DEFAULT_PAGE } from '../../../../components/Table/constants'
import { useDispatch } from 'react-redux'
import axios, { CancelTokenSource } from 'axios'
import { useDebounce } from '../../../../hooks/hooks'
import { setLoading } from '../../../../redux/store/common/slice'
import { getMyVendorsRequest } from './api'
import { TMyVendorRequestData, TMyVendors, TMyVendorsFilters } from './types'

export const MyVendors = () => {
  const dispatch = useDispatch()

  const timeoutRef = useRef<ReturnType<typeof setTimeout>>()
  const tableDataTokenRef = useRef<CancelTokenSource>()

  const [tableData, setTableData] = useState<TMyVendors[]>([])
  const [searchValue, setSearchValue] = useState('')
  const [sortParams, setSortParams] = useState<TMyVendorRequestData>()
  const [firstLoad, setFirstLoad] = useState(true)
  const [totalNumber, setTotalNumber] = useState(0)
  const [initialTotalNumber, setInitialTotalNumber] = useState(0)
  const [pageInfo, setPageInfo] = useState({ ...DEFAULT_PAGE })

  const debouncedSearch = useDebounce(searchValue, 500)

  const [filters, setFilters] = useState<TMyVendorsFilters>({})

  useEffect(() => {
    if (timeoutRef.current) {
      clearTimeout(timeoutRef.current)
    }
    timeoutRef.current = setTimeout(
      () => {
        getTableData({ ...sortParams, filters, page: { ...DEFAULT_PAGE } })
        setPageInfo({ ...DEFAULT_PAGE })
      },
      firstLoad ? 0 : 500
    )
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  useEffect(() => {
    return () => {
      if (!!tableDataTokenRef.current) {
        tableDataTokenRef.current.cancel()
      }
    }
  }, [])

  const applyFilters: TVendorsListFiltersFunc = (field, appliedFilters) => {
    const newFilters = { ...filters, [field]: appliedFilters }
    const newPageInfo = { ...pageInfo, pageNumber: 1 }
    setFilters(newFilters)
    setPageInfo(newPageInfo)
    getTableData({ ...sortParams, filters: newFilters, page: newPageInfo })
  }

  const getTableData = ({
    sortField,
    sortOrder,
    filters,
    page
  }: TMyVendorRequestData) => {
    dispatch(setLoading(true))

    if (!!tableDataTokenRef.current) {
      tableDataTokenRef.current.cancel()
    }
    const params: TVendorListParams = { ...filters }

    if (searchValue) {
      params.search = searchValue
    }

    if (sortField) {
      if (sortOrder) {
        params.ordering = `${sortOrder === 'descend' ? '-' : ''}${sortField}`
      }
      setSortParams({
        sortField,
        sortOrder
      })
    }

    const dataPage = page ? page : pageInfo

    params.limit = dataPage.pageSize
    params.offset = (dataPage.pageNumber - 1) * dataPage.pageSize
    tableDataTokenRef.current = axios.CancelToken.source()

    getMyVendorsRequest({
      cancelToken: tableDataTokenRef.current?.token,
      params
    })
      .then((resp) => {
        if (!resp.data?.results) {
          return
        }
        setTableData(
          resp.data.results.map((i) => ({
            ...i,
            key: i.uuid
          }))
        )
        setTotalNumber(resp.data.count)
        if (firstLoad) {
          setInitialTotalNumber(resp.data.count)
          setFirstLoad(false)
        }
      })
      .finally(() => dispatch(setLoading(false)))
  }

  const handleTableChange = (pagination, _filters, sorter) => {
    const page = {
      pageNumber: pagination.current,
      pageSize: pagination.pageSize
    }
    getTableData({
      sortField: sorter.field,
      sortOrder: sorter.order,
      filters,
      page
    })
    setPageInfo(page)
  }

  const handleChangePageSize = (pageSize) => {
    const page = {
      pageNumber: 1,
      pageSize
    }
    getTableData({
      ...sortParams,
      filters,
      page
    })
    setPageInfo(page)
  }

  return (
    <div className="table-wrapper mt-24">
      {!firstLoad &&
        (!!tableData.length || initialTotalNumber ? (
          <>
            <div className="table-wrapper__header">
              <Input
                className="allow-clear"
                propsInput={{
                  maxLength: 20,
                  allowClear: { clearIcon: <Close16 /> },
                  placeholder: 'Search',
                  prefix: <Search16 />,
                  value: searchValue,
                  onChange: (e) => setSearchValue(e.target.value)
                }}
              />
            </div>
            <Table<TMyVendors>
              dataSource={tableData}
              columns={columns({
                applyFilters,
                filters
              })}
              className="vendor-data-list-table"
              onChange={handleTableChange}
              hasSearchOrFilters={isTableHasParams<TMyVendorsFilters>(
                debouncedSearch,
                filters
              )}
              onChangePage={handleChangePageSize}
              pageSize={pageInfo.pageSize}
              pagination={{
                pageSize: pageInfo.pageSize,
                current: pageInfo.pageNumber,
                total: totalNumber
              }}
            />
          </>
        ) : (
          <EmptyState />
        ))}
    </div>
  )
}
